/**
 * PLAB-06 (#79): load the Zendesk widget on demand instead of on every page load.
 *
 * The widget was a parser-blocking `<script id="ze-snippet" src="...">` at the end of `<body>`
 * (layout/theme.liquid:454 before this change), costing 171 ms of main thread on every page,
 * measured on Slow 4G with 4x CPU throttling, for a widget most visitors never open. Nothing
 * about it is needed before a visitor asks for help, so it now waits for one of:
 *
 *   - the first pointerdown, keydown, touchstart or scroll anywhere on the page;
 *   - a click on any element carrying `data-wf-help` (no element carries it today - it is the
 *     hook for a future Help affordance in the header or footer);
 *   - the browser going idle after `load`, so the launcher still appears for a visitor who only
 *     reads.
 *
 * Whichever fires first wins; the rest are removed, so a long session does not keep a
 * capture-phase handler on every scroll. The snippet URL and its widget key stay in Liquid, read
 * from the carrier tag's `data-wf-zendesk-src`, so nothing about where the key lives changes.
 *
 * NO `integrity=`. Zendesk publishes no Subresource Integrity hash for `snippet.js` and serves
 * content that changes per request, so a hash would make the browser refuse to run it. The tag it
 * replaced never carried one either.
 *
 * NO GLOBALS AND NO BARE TIMERS. Everything is reached through an injected `scope` - the window
 * in the browser, a plain object in the tests. That is also what keeps the module inside the
 * ESLint globals allowlist for `assets/woopf-*.js`, which grants `window`, `document` and
 * `navigator` but neither `setTimeout` nor `requestIdleCallback`.
 *
 * TAG-05 (#368): once the snippet has loaded, `listenForChat` attaches Zendesk Messaging's
 * `zE('messenger:on', 'open', ...)` listener and pushes `chat_opened` to `dataLayer` - the event
 * name alone, no shopper-written text - every time the messenger opens. A Classic widget or a
 * blocked snippet, where `window.zE` is missing or refuses the command, is not an error. Zendesk
 * also reopens a messenger the shopper left open on the next page, with no shopper action behind
 * it; `listenForChat` also attaches a `'close'` listener and records the open/closed state under
 * `sessionStorage`'s `CHAT_STATE_KEY` so that reopen pushes nothing, falling back to pushing on
 * every open when storage is unavailable or throws.
 *
 * Unit tests: `tests/privacy/zendesk-defer.test.ts`, `tests/assets/zendesk-chat-event.test.mjs`.
 */

/** The id Zendesk's own snippet looks for, and what makes injection idempotent. */
export const ZENDESK_SCRIPT_ID = 'ze-snippet';

/** Where Liquid keeps the snippet URL, so the widget key never moves into JavaScript. */
export const ZENDESK_SOURCE_ATTRIBUTE = 'data-wf-zendesk-src';

/** Any affordance that offers help: clicking one loads the widget immediately. */
export const HELP_SELECTOR = '[data-wf-help]';

/** Events that count as "this visitor is here and doing something". */
export const WAKE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

/** How long after `load` the idle fallback waits before giving up and loading anyway. */
export const IDLE_TIMEOUT_MS = 5000;

/** TAG-05 (#368): pushed each time the Zendesk messenger opens. */
export const CHAT_OPENED = 'chat_opened';

/**
 * TAG-05 (#368): sessionStorage key recording whether the messenger is currently open, so a
 * reopen Zendesk performs on a new page - the shopper never touched anything - does not push a
 * second chat_opened.
 */
export const CHAT_STATE_KEY = 'wf-chat-open';

/**
 * TAG-05 (#368): attach the messenger's open and close listeners. Called once the snippet has
 * loaded, when window.zE exists. A missing or refusing zE (a Classic widget, a blocked snippet) is
 * not an error.
 *
 * On open: when sessionStorage already holds `'1'` for CHAT_STATE_KEY, the messenger was already
 * open (Zendesk restoring it on a new page), so nothing is pushed; otherwise `chat_opened` is
 * pushed. Either way the state is then stored as `'1'`. On close, the state is stored as `'0'`.
 * Every storage read and write is wrapped in try/catch; storage that is unavailable or throws is
 * never an error - it just falls back to pushing on every open, as before this fix.
 *
 * @param {Window} scope
 * @returns {boolean} true when the listeners were registered
 */
export const listenForChat = (scope) => {
  if (!scope || typeof scope.zE !== 'function') return false;

  const wasOpen = () => {
    try {
      return scope.sessionStorage.getItem(CHAT_STATE_KEY) === '1';
    } catch {
      return false;
    }
  };

  const storeState = (value) => {
    try {
      scope.sessionStorage.setItem(CHAT_STATE_KEY, value);
    } catch {
      // Storage unavailable or throwing is never an error: falls back to pushing every open.
    }
  };

  try {
    scope.zE('messenger:on', 'open', () => {
      const alreadyOpen = wasOpen();
      storeState('1');
      if (alreadyOpen) return;
      scope.dataLayer = scope.dataLayer || [];
      scope.dataLayer.push({ event: CHAT_OPENED });
    });
    scope.zE('messenger:on', 'close', () => {
      storeState('0');
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Read the snippet URL off the carrier tag.
 *
 * @param {Document} doc
 * @returns {string} the snippet URL, or '' when the tag is missing
 */
export const readSource = (doc) => {
  if (!doc || typeof doc.querySelector !== 'function') return '';
  const tag = doc.querySelector(`script[${ZENDESK_SOURCE_ATTRIBUTE}]`);
  if (!tag || typeof tag.getAttribute !== 'function') return '';
  return tag.getAttribute(ZENDESK_SOURCE_ATTRIBUTE) || '';
};

/**
 * Inject the widget. Idempotent by id, which is also the id Zendesk itself looks for, so a
 * second call - or a Theme Editor re-render - can never produce two widgets.
 *
 * @param {Document} doc
 * @param {string} source
 * @param {() => void} [onLoad] TAG-05 (#368): called once the snippet has finished loading, so the
 *   caller can attach the messenger's open listener. Optional; existing callers are unchanged.
 * @returns {boolean} true when this call injected the script
 */
export const loadZendesk = (doc, source, onLoad) => {
  if (!doc || !source || typeof doc.createElement !== 'function') return false;
  if (typeof doc.getElementById === 'function' && doc.getElementById(ZENDESK_SCRIPT_ID)) {
    return false;
  }
  const script = doc.createElement('script');
  script.id = ZENDESK_SCRIPT_ID;
  script.src = source;
  script.async = true;
  if (typeof onLoad === 'function' && typeof script.addEventListener === 'function') {
    script.addEventListener('load', onLoad);
  }
  if (doc.body && typeof doc.body.appendChild === 'function') doc.body.appendChild(script);
  return true;
};

/**
 * Wire every trigger.
 *
 * @param {Window} scope
 * @returns {() => void} a trigger that loads the widget now. The listeners call it themselves;
 *   it is returned so a caller that needs the widget before a visitor has touched anything can
 *   force the load, and so the tests can prove one call is enough. It is safe to call at any
 *   time and safe to call twice.
 */
export const initZendeskDefer = (scope) => {
  const doc = scope && scope.document;
  if (!doc) return () => {};

  const source = readSource(doc);
  if (!source) return () => {};

  let done = false;
  const teardowns = [];

  const fire = () => {
    if (done) return;
    done = true;
    for (const off of teardowns) off();
    teardowns.length = 0;
    loadZendesk(doc, source, () => listenForChat(scope));
  };

  const on = (target, type, handler, options) => {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(type, handler, options);
    teardowns.push(() => {
      if (typeof target.removeEventListener === 'function') {
        target.removeEventListener(type, handler, options);
      }
    });
  };

  for (const type of WAKE_EVENTS) {
    on(doc, type, fire, { once: true, passive: true, capture: true });
  }

  on(
    doc,
    'click',
    (event) => {
      const target = event && event.target;
      const matched =
        target && typeof target.closest === 'function' ? target.closest(HELP_SELECTOR) : null;
      if (matched) fire();
    },
    { capture: true },
  );

  const idle = () => {
    if (typeof scope.requestIdleCallback === 'function') {
      scope.requestIdleCallback(fire, { timeout: IDLE_TIMEOUT_MS });
    } else if (typeof scope.setTimeout === 'function') {
      scope.setTimeout(fire, IDLE_TIMEOUT_MS);
    }
  };

  if (doc.readyState === 'complete') idle();
  else on(scope, 'load', idle, { once: true });

  return fire;
};

/*
 * The auto-start. `typeof document` guards the Vitest run, which has no DOM at all.
 */
if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  initZendeskDefer(window);
}
